import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import APICall from "../../APICall";
import EndPoints from "../../APICall/EndPoints";
import DashboardCommonTable from "../../components/DashboardCommonTable";
import { Col, Spin, notification, Tooltip, Row } from "antd";
import EditTrade from "../../components/EditTrade/EditTrade";
import VerificationModal from "../../components/VerificationModal/VerificationModal";
import {
  ClientNameCol,
  DeleteIcon,
  EditIcon,
  RetweetOutIcon,
  SmallTag,
  StyledOverFlowDiv,
  TradeTableActionCol,
  onlyTime,
} from "./style";
import {
  findMarket,
  formatNumber,
} from "../../components/FormateToINR/formatToINR";
import {
  MemoizedCardCurrentPrice,
  limitNumberLength,
} from "../../components/SocketLiveData";
import { useSocket } from "../../components/Socket";
import store from "../../store/store";
import { setScriptCall } from "../../store/slice/globleSlice";
import { useDeleteTrade } from "../../APICall/apiHooks";
import { MobileOutlined, DesktopOutlined } from "@ant-design/icons";
import { UplineTag } from "../Users/Accounts/style";
import { labelObj, marketTag } from "../../Helper/constant";
import { MarketTableTag } from "../MarketWatch/style";

const currentPrice = (record) => {
  let key = record?.tradePattern === "sell" ? "Ask" : "Bid";
  return key;
};
const PendingTradeTable = React.memo(({ customerScreen, filterValue }) => {
  const queryClient = useQueryClient();

  //state
  const [showModal, setShowModal] = useState({
    name: null,
    flag: false,
    record: null,
  });
  const [executeLoading, setExecuteLoading] = useState(false);
  const [symbol, setSymbol] = useState([]);
  const dispatch = useDispatch();
  const socketContext = useSocket();
  const { doSend } = socketContext;

  // initial function
  const handleCancel = () => {
    setShowModal({ name: null, flag: false, record: null });
  };
  // custom hooks for delete trade
  const { deleteTradeData, deleteTradeLoading, deleteTradeMutate } =
    useDeleteTrade({
      key: ["pendingTrade"],
      handleCancel,
    });

  //redux
  const LoginDetails = useSelector((state) => state?.authSlice?.userDetail);
  const loginUserId = LoginDetails?.userId;
  const loginType = LoginDetails?.accountType;
  const isAllow = loginType !== "broker";
  const isCustomer = loginType === "customer";
  const isStale = queryClient.getQueryCache().find(["pendingTrade"])?.isStale();

  // api call
  const pendingQuery = async ({ pageParam = 1 }) => {
    let obj = { userId: loginUserId, tradeStatus: "pending" };
    if (filterValue) obj = { ...obj, ...filterValue };
    let url = `${EndPoints.getTrade}?page=${pageParam}&limit=10`;
    // if (!customerScreen) url += "&isLatest=true";
    let response = await APICall("post", url, obj);
    return response?.data?.data;
  };

  const pendingTrade = useInfiniteQuery({
    queryKey: customerScreen ? ["pendingTrade", filterValue] : ["pendingTrade"],
    queryFn: pendingQuery,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    refetchOnMount: customerScreen ? true : false,
  });

  if (pendingTrade?.error) {
    notification.error({ message: pendingTrade?.error?.message });
  }

  const pendingTradeData = pendingTrade?.data?.pages?.flatMap(
    (page) => page?.trades
  );

  // Get totalCount from the first page of API response
  const totalCount = pendingTrade?.data?.pages?.[0]?.totalCount || 0;

  //life cycle
  useEffect(() => {
    if (isStale) {
      queryClient.invalidateQueries({ queryKey: ["pendingTrade"] });
    }

    return () => {
      if (symbol?.length > 0) {
        doSend({
          event: "unsubscribeToServerMarket",
          payload: symbol,
        });
        dispatch(setScriptCall([]));
      }
    };
  }, []);

  useEffect(() => {
    if (
      pendingTrade.data?.pages &&
      !pendingTrade?.isLoading &&
      !pendingTrade?.isFetching
    ) {
      const scriptList = pendingTradeData?.map((el) => el?.symbol) || 0;
      const finalList = [...new Set(scriptList)];
      setSymbol(finalList);
      if (finalList?.length > 0) {
        doSend({
          event: "subscribeToServerMarket",
          payload: finalList,
        });
      }
      dispatch(setScriptCall(finalList));
    }
  }, [pendingTrade?.isLoading, pendingTrade?.isFetching]);

  // api call
  const handleExecute = async (record, limitType, payloadPrice) => {
    const id = record?._id;
    const customerId = record?.customer[0]?.userId;
    const valueType = currentPrice(record);
    const price =
      store.getState()?.webSocketSlice?.webSocket?.data?.[record?.symbol]?.[
      valueType
      ];
    const ltp =
      store.getState()?.webSocketSlice?.webSocket?.data?.[record?.symbol]
        ?.LTP || 0;

    const obj = {
      tradeStatus: "executed",
      tradePrice: payloadPrice,
      executionType: limitType,
      stoploss:
        record?.marketName === "GLOBEX" || record?.marketName === "FOREX"
          ? price
          : ltp,
    };
    setExecuteLoading(true);
    try {
      let response = await APICall(
        "patch",
        `${EndPoints.editTrade}${id}&customerId=${customerId}`,
        obj
      );
      if (response.status === 200) {
        notification.success({ message: response?.data?.message });
        queryClient.refetchQueries({
          queryKey: ["pendingTrade"],
          type: "active",
        });
        setShowModal({ name: null, flag: false, record: null });
      } else {
        notification.error({ message: response?.data?.message });
      }
      // console.log("response", response);
    } catch (error) {
      console.log("error", error);
    } finally {
      setExecuteLoading(false);
    }
  };

  // function
  const handleDeleteTrade = async (record) => {
    let obj = {
      deviceType: "web",
      id: record?._id,
      tradeIpAddress: record?.tradeIpAddress,
      tradeStatus: "pending",
    };
    deleteTradeMutate(obj);
  };
  const handleEdit = (record, key) => {
    setShowModal({ name: "edit", flag: true, record: { ...record, key } });
  };

  // column
  const pendingTradeColumns = useMemo(
    () => [
      {
        title: "",
        dataIndex: "index",
        key: "index",
        align: "left",
        render: (text, record, index) => {
          return (
            <div>
              {record?.deviceType === "mob" ? <MobileOutlined /> : record?.deviceType === "web" ? <DesktopOutlined /> : "-"}
            </div>
          );
        },
      },
      {
        title: "TIME",
        dataIndex: "time",
        key: "time",
        flex: 1,
        align: "left",
        render: (text, record) => {
          const time = onlyTime(record?.tradeTime);
          return <span>{time}</span>;
        },
      },
      {
        title: "CLIENT",
        dataIndex: "client",
        flex: 1,
        key: "client",
        ellipsis: true,
        align: "left",
        render: (text, record) => {
          let id = record?.customer[0]?.userId;
          return (
            <>
              {/* <Tooltip
              title={`${id} (${record?.customer[0]?.accountName})`}
              placement="top"
            > */}
              <div>
                <div style={{ fontWeight: "bold" }}>{id}</div>
                <div >{record?.customer[0]?.accountName}</div>
              </div>
              {/* </Tooltip> */}
            </>
          );
        },
      },
      {
        title: "UPLINE",
        dataIndex: "upline",
        key: "upline",
        align: "left",
        flex: 1,
        ellipsis: true,
        render: (text, record) => {
          let id = record?.customer[0]?.parent?.userId;
          return (
            <>
              {/* <Tooltip
              title={`${id} (${record?.customer[0]?.parent?.accountName})`}
              placement="top"
            > */}
              <div>
                <div style={{ fontWeight: "bold" }}>{id}<UplineTag color="magenta" bordered={false}>
                  {labelObj[record?.customer[0]?.parent?.accountType]}
                </UplineTag></div>
                <div >{record?.customer[0]?.parent?.accountName}</div>
              </div>
              {/* </Tooltip> */}
            </>
          );
        },
      },
      {
        title: "MARKET",
        dataIndex: "marketName",
        key: "marketName",
        flex: 1,
        render: (text, record) => {
          return <div>
            {/* <Tooltip title={record?.marketName} placement={"leftTop"}> */}
            <MarketTableTag color="volcano-inverse">
              {marketTag[record?.marketName]}
            </MarketTableTag>
            {/* </Tooltip> */}
          </div>;
        },
      },
      {
        title: "SCRIPT",
        dataIndex: "symbol",
        key: "scriptName",
        flex: 1,
        align: "left",
        render: (text, record) => {
          const date = record?.expiryDate ? record?.expiryDate : "";
          // const date = valanBillDate(record?.expiryDate, false);
          const opt = record?.marketName?.endsWith("OPT");
          const name = opt
            ? `${record?.symbolName} ${record?.series ? record?.series : ""} ${record?.strikePrice ? record?.strikePrice : ""
            }`
            : record?.symbolName;
          return (
            <div>
              <div style={{ textAlign: "left", display: "flex" }}>
                {name}  <SmallTag color="orange" variant="solid" bordered={false}>
                  {record?.title}
                </SmallTag>
                <SmallTag
                  color={record?.tradePattern === "buy" ? "green" : "red"}
                  variant="solid"
                  bordered={false}
                >
                  {record?.tradePattern.toUpperCase()}
                </SmallTag>
              </div>
              <div style={{ textAlign: "left", fontSize: "10px", fontWeight: "bold" }}>
                {date}<text style={{ color: "#728780" }}> ({record?.tradeNumber})</text>
              </div>

            </div>
          );
        },
      },

      {
        title: "QTY (LOT)",
        dataIndex: "lot",
        key: "lot",
        flex: 1,
        align: "left",
        render: (text, record) => {
          const lot = formatNumber(record?.lot);
          const quantity = formatNumber(record?.quantity);
          const data =
            record?.marketName === "NSE EQU"
              ? quantity
              : `${quantity} (${lot})`;
          return <div>{data}</div>;
        },
      },
      {
        title: "RATE",
        dataIndex: "tradePrice",
        key: "tradePrice",
        flex: 1,
        align: "center",
        render: (text, record) => {
          const tradePrice = findMarket(record?.marketName)
            ? limitNumberLength(record?.tradePrice)
            : record?.tradePrice?.toFixed(2);
          return <div>{tradePrice}</div>;
        },
      },
      {
        title: "Live",
        dataIndex: "live",
        key: "live",
        align: "center",
        flex: 1,
        render: (text, record) => {
          return (
            <>
              <MemoizedCardCurrentPrice
                item={record}
                priceType={currentPrice(record)}
              />
            </>
          );
        },
      },
      isAllow
        ? {
          title: "ACTION",
          dataIndex: "edit",
          key: "edit",
          flex: 1,
          align: "center",
          render: (text, record, index) => {
            return (
              <TradeTableActionCol>
                <EditIcon
                  onClick={() => handleEdit(record, "pendingTrade")}
                />

                {deleteTradeLoading ? (
                  <Spin size="small" />
                ) : (
                  <DeleteIcon
                    onClick={() => {
                      isCustomer
                        ? handleDeleteTrade(record)
                        : setShowModal({
                          name: "delete",
                          flag: true,
                          record: record,
                        });
                    }}
                  />
                )}

                <RetweetOutIcon
                  onClick={() =>
                    isCustomer
                      ? handleExecute(record)
                      : setShowModal({
                        name: "execute",
                        flag: true,
                        record: record,
                      })
                  }
                />
                {/* </Popconfirm> */}
              </TradeTableActionCol>
            );
          },
        }
        : {
          flex: 1,
        },
    ],
    [handleExecute, pendingTradeData]
  );

  return (
    <Col xxl={12} xl={12} lg={24}>
      <DashboardCommonTable
        columns={pendingTradeColumns}
        tableTitle="PENDING ORDERS"
        dataSource={pendingTradeData?.[0] ? pendingTradeData : []}
        viewMore={true}
        allowBtn={false}
        queryKey="pendingTrade"
        isLoading={pendingTrade?.isLoading}
        tradeQuery={pendingTrade}
        emptyPage={pendingTrade?.data?.pages?.[0]?.trades?.length === 0}
        handleEdit={handleEdit}
        handleExecute={handleExecute}
        handleDeleteTrade={handleDeleteTrade}
        deleteTradeLoading={deleteTradeLoading}
      />
      {showModal?.flag && showModal?.name === "edit" && (
        <EditTrade
          handleCancel={handleCancel}
          record={showModal?.record}
          queryKey={["pendingTrade"]}
          pendingSymbol={symbol}
        />
      )}

      {showModal?.flag && showModal?.name === "delete" && (
        <VerificationModal
          handleCancel={handleCancel}
          record={showModal?.record}
          query={["pendingTrade"]}
          type="delete"
          title={"Delete Trade"}
        />
      )}
      {showModal?.flag && showModal?.name === "execute" && (
        <VerificationModal
          handleCancel={handleCancel}
          record={showModal?.record}
          handleExecute={handleExecute}
          executeLoading={executeLoading}
          query={["pendingTrade"]}
          type="execute"
          title={"Execute Pending Trade"}
          customPrice={true}
        />
      )}
    </Col>
  );
});

export default PendingTradeTable;
